import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Label } from '../ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog';
import { toast } from 'sonner';
import { Plus, Edit2, Trash2, Save, X, CheckCircle, XCircle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LeavePortalEmbed = ({ impersonatedUser, adminUser }) => {
  const [balance, setBalance] = useState(null);
  const [leaves, setLeaves] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Dialog state
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLeave, setEditingLeave] = useState(null);
  const [formData, setFormData] = useState({
    leave_type: 'AL',
    leave_category: 'full_day',
    from_date: '',
    to_date: '',
    reason: '',
    approval_status: 'approved'
  });

  const leaveTypeLabels = {
    AL: 'Annual Leave',
    OH: 'Organization Holiday',
    RH: 'Restricted Holiday',
    LWP: 'Leave Without Pay'
  };

  const leaveCategories = ['full_day', 'half_day', 'multiple_days'];

  useEffect(() => {
    fetchData();
  }, [impersonatedUser]);

  const fetchData = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const response = await axios.get(
        `${API}/superadmin/impersonate/leaves/${impersonatedUser.employee_id}`,
        config
      );
      
      setBalance(response.data.balance || {});
      setLeaves(response.data.leaves || []);
    } catch (error) {
      console.error('Error fetching leave data:', error);
      toast.error('Unable to fetch leave data');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const badges = {
      approved: 'bg-green-100 text-green-800',
      pending: 'bg-yellow-100 text-yellow-800',
      rejected: 'bg-red-100 text-red-800'
    };
    return badges[status] || 'bg-gray-100 text-gray-800';
  };

  const handleOpenCreate = () => {
    setEditingLeave(null);
    setFormData({
      leave_type: 'AL',
      leave_category: 'full_day',
      from_date: '',
      to_date: '',
      reason: '',
      approval_status: 'approved'
    });
    setDialogOpen(true);
  };

  const handleOpenEdit = (leave) => {
    setEditingLeave(leave);
    setFormData({
      leave_type: leave.leave_type || 'AL',
      leave_category: leave.leave_category || 'full_day',
      from_date: leave.from_date || '',
      to_date: leave.to_date || '',
      reason: leave.reason || '',
      approval_status: leave.approval_status || 'approved'
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.from_date || !formData.to_date) {
      toast.error('Please fill in all required fields');
      return;
    }

    try {
      setSaving(true);
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      const payload = {
        portal: 'leave',
        employee_id: impersonatedUser.employee_id,
        data: {
          ...formData,
          employee_id: impersonatedUser.employee_id,
          employee_name: impersonatedUser.name,
          submitted_at: new Date().toISOString()
        }
      };

      if (editingLeave) {
        await axios.put(`${API}/superadmin/entries/update`, {
          ...payload,
          record_id: editingLeave.id || editingLeave.from_date
        }, config);
        toast.success('Leave updated successfully');
      } else {
        await axios.post(`${API}/superadmin/entries/create`, payload, config);
        toast.success('Leave created successfully');
      }

      setDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error('Error saving leave:', error);
      toast.error(error.response?.data?.detail || 'Failed to save leave');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (leave) => {
    if (!window.confirm('Are you sure you want to delete this leave?')) return;

    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.delete(`${API}/superadmin/entries/delete`, {
        ...config,
        data: {
          portal: 'leave',
          employee_id: impersonatedUser.employee_id,
          record_id: leave.id || leave.from_date
        }
      });

      toast.success('Leave deleted successfully');
      fetchData();
    } catch (error) {
      console.error('Error deleting leave:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete leave');
    }
  };

  const handleApprove = async (leave) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(`${API}/superadmin/entries/update`, {
        portal: 'leave',
        employee_id: impersonatedUser.employee_id,
        record_id: leave.id || leave.from_date,
        data: { ...leave, approval_status: 'approved' }
      }, config);

      toast.success('Leave approved');
      fetchData();
    } catch (error) {
      toast.error('Failed to approve leave');
    }
  };

  const handleReject = async (leave) => {
    try {
      const token = localStorage.getItem('token');
      const config = { headers: { Authorization: `Bearer ${token}` } };

      await axios.put(`${API}/superadmin/entries/update`, {
        portal: 'leave',
        employee_id: impersonatedUser.employee_id,
        record_id: leave.id || leave.from_date,
        data: { ...leave, approval_status: 'rejected' }
      }, config);

      toast.success('Leave rejected');
      fetchData();
    } catch (error) {
      toast.error('Failed to reject leave');
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6" data-testid="leave-portal-embed">
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
        {/* Leave Balance */}
        <Card className="lg:col-span-1">
          <CardHeader>
            <CardTitle className="text-base">Leave Balance</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="p-3 bg-blue-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 mb-2">Annual Leave</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Available</span>
                    <span className="font-semibold">{balance?.AL?.available || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Used</span>
                    <span className="font-semibold">{balance?.AL?.used || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Total</span>
                    <span className="font-semibold">{balance?.AL?.total || 0}</span>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-green-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 mb-2">Organization Holiday</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Available</span>
                    <span className="font-semibold">{balance?.OH?.available || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Used</span>
                    <span className="font-semibold">{balance?.OH?.used || 0}</span>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-purple-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 mb-2">Restricted Holiday</h3>
                <div className="space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Available</span>
                    <span className="font-semibold">{balance?.RH?.available || 0}</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Used</span>
                    <span className="font-semibold">{balance?.RH?.used || 0}</span>
                  </div>
                </div>
              </div>

              <div className="p-3 bg-red-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-600 mb-2">Leave Without Pay</h3>
                <div className="text-sm">
                  <div className="flex justify-between">
                    <span>Used</span>
                    <span className="font-semibold">{balance?.LWP?.used || 0}</span>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Leave History */}
        <Card className="lg:col-span-3">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Leave History for {impersonatedUser.name}</CardTitle>
              <Button onClick={handleOpenCreate} size="sm" className="bg-purple-600 hover:bg-purple-700">
                <Plus className="w-4 h-4 mr-1" /> Add Leave
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Leave Type</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>From Date</TableHead>
                  <TableHead>To Date</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {leaves.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-gray-500">
                      No leave applications found
                    </TableCell>
                  </TableRow>
                ) : (
                  leaves.slice(0, 15).map((leave, idx) => (
                    <TableRow key={leave.id || idx}>
                      <TableCell>{leaveTypeLabels[leave.leave_type] || leave.leave_type}</TableCell>
                      <TableCell className="capitalize">{leave.leave_category?.replace('_', ' ')}</TableCell>
                      <TableCell>{leave.from_date}</TableCell>
                      <TableCell>{leave.to_date}</TableCell>
                      <TableCell className="max-w-xs truncate">{leave.reason}</TableCell>
                      <TableCell>
                        <Badge className={getStatusBadge(leave.approval_status)}>
                          {leave.approval_status}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className="flex gap-1">
                          {leave.approval_status === 'pending' && (
                            <>
                              <Button size="sm" variant="ghost" onClick={() => handleApprove(leave)} title="Approve">
                                <CheckCircle className="w-4 h-4 text-green-600" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => handleReject(leave)} title="Reject">
                                <XCircle className="w-4 h-4 text-red-600" />
                              </Button>
                            </>
                          )}
                          <Button size="sm" variant="ghost" onClick={() => handleOpenEdit(leave)}>
                            <Edit2 className="w-4 h-4 text-blue-600" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => handleDelete(leave)}>
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingLeave ? 'Edit Leave' : 'Create Leave'}</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Leave Type</Label>
                <Select value={formData.leave_type} onValueChange={(v) => setFormData({...formData, leave_type: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(leaveTypeLabels).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Category</Label>
                <Select value={formData.leave_category} onValueChange={(v) => setFormData({...formData, leave_category: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {leaveCategories.map(cat => (
                      <SelectItem key={cat} value={cat}>{cat.replace('_', ' ')}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={formData.from_date}
                  onChange={(e) => setFormData({...formData, from_date: e.target.value})}
                />
              </div>
              <div>
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={formData.to_date}
                  onChange={(e) => setFormData({...formData, to_date: e.target.value})}
                />
              </div>
            </div>

            <div>
              <Label>Reason</Label>
              <Textarea
                value={formData.reason}
                onChange={(e) => setFormData({...formData, reason: e.target.value})}
                placeholder="Enter reason for leave"
              />
            </div>

            <div>
              <Label>Status</Label>
              <Select value={formData.approval_status} onValueChange={(v) => setFormData({...formData, approval_status: v})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Cancel
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-purple-600 hover:bg-purple-700">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Saving...' : 'Save'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeavePortalEmbed;
